import type {
    OrderRestaurantShortDTO,
    RestaurantShortDTO,
    StoreLocatorDTO,
    StoreLocatorMarkersDTO
} from 'shared/types'
import endpoints from 'shared/store/endpoints'
import type { Action as ActionMetadata } from 'shared/store/metadata'
import { pageMetadata } from 'shared/store/metadata'
import { deliveryClient, ws } from 'shared/store/ws'
import type { RootState } from 'shared/store/reducers'
import type {
    Coordinates,
    RestaurantVeryShortDTO,
    ResultsType,
    SearchParams
} from './types'
import type { ActionAPIError, ActionNetworkError } from 'shared/store/actions'
import { actionNetworkError } from 'shared/store/actions'
import { errors, ecommerceType } from 'shared/utils'
import { getEcommerceAxiosClient } from 'shared/utils/ecommerce/utils'

const { DELIVERY_IN_APP } = ecommerceType

type ActionLoading = {
    type: 'LOADING',
    status: boolean
}

const loading = (status: boolean): ActionLoading => ({
    type: 'LOADING',
    status
})

type ActionReset = {
    type: 'RESET'
}

const reset = (): ActionReset => ({
    type: 'RESET'
})

type ActionRestaurantLoading = {
    type: 'RESTAURANT_LOADING',
    status: boolean
}

const restaurantLoading = (status: boolean): ActionRestaurantLoading => ({
    type: 'RESTAURANT_LOADING',
    status
})

type ActionSearchResults = {
    type: 'SEARCH_RESULTS',
    resultsType: ResultsType,
    address?: string,
    geocodedAddress: Coordinates,
    restaurants: Array<OrderRestaurantShortDTO>,
    fromDelivery?: boolean
}

type ActionSearchAllResults = {
    type: 'SEARCH_ALL_RESULTS',
    data: Array<RestaurantVeryShortDTO>
}

const actionSearchResults = (
    address?: string,
    geocodedAddress: Coordinates,
    resultsType: ResultsType,
    restaurants: Array<OrderRestaurantShortDTO>,
    fromDelivery?: boolean
): ActionSearchResults => {
    return {
        type: 'SEARCH_RESULTS',
        resultsType,
        address,
        geocodedAddress,
        restaurants,
        fromDelivery
    }
}

const actionSearchAllResults = (
    response: Array<RestaurantVeryShortDTO>
): ActionSearchAllResults => {
    return {
        type: 'SEARCH_ALL_RESULTS',
        data: response
    }
}

type ActionClearSearch = {
    type: 'CLEAR_SEARCH',
    fromDelivery?: boolean
}
const clearSearch = (fromDelivery?: boolean): ActionClearSearch => ({
    type: 'CLEAR_SEARCH',
    fromDelivery
})

type ActionStoreLocatorPageDataResponse = {
    type: 'PAGE_DATA_STORELOCATOR_RESPONSE',
    data: StoreLocatorDTO
}
const actionStoreLocatorPageDataResponse = (
    response: StoreLocatorDTO
): ActionStoreLocatorPageDataResponse => {
    return {
        type: 'PAGE_DATA_STORELOCATOR_RESPONSE',
        data: response
    }
}

type ActionSelectRestaurant = {
    type: 'SELECT_RESTAURANT',
    restaurant: ?RestaurantShortDTO
}
const actionSelectRestaurant = (
    restaurant: ?RestaurantShortDTO
): ActionSelectRestaurant => ({
    type: 'SELECT_RESTAURANT',
    restaurant
})

type ActionToggleRestaurant = {
    type: 'TOGGLE_RESTAURANT'
}
const toggleRestaurant = (): ActionToggleRestaurant => ({
    type: 'TOGGLE_RESTAURANT'
})

// Anti pattern
type ActionSetVisitedRestaurants = {
    type: 'SET_VISITED_RESTAURANTS',
    visitedRestaurants: Array<RestaurantShortDTO>
}
const setVisitedRestaurants = (
    visitedRestaurants: Array<RestaurantShortDTO>
): ActionSetVisitedRestaurants => ({
    type: 'SET_VISITED_RESTAURANTS',
    visitedRestaurants
})

type ActionSetUserPosition = {
    type: 'SET_USER_POSITION',
    userPosition: ?Coordinates
}
const setUserPosition = (
    userPosition: ?Coordinates
): ActionSetUserPosition => ({
    type: 'SET_USER_POSITION',
    userPosition
})

type ActionSelectedServices = {
    type: 'SELECTED_SERVICES',
    selectedServices: Array<string>
}
const setSelectedServices = (
    selectedServices: Array<string>
): ActionSelectedServices => ({
    type: 'SELECTED_SERVICES',
    selectedServices
})

type ActionResetServices = {
    type: 'RESET_SERVICES'
}
const resetServices = (): ActionResetServices => ({
    type: 'RESET_SERVICES'
})

type ActionCenterOnUserPosition = {
    type: 'CENTER_ON_USER_POSITION'
}
const centerOnUserPosition = (): ActionCenterOnUserPosition => ({
    type: 'CENTER_ON_USER_POSITION'
})

type ActionMapCenter = {
    type: 'MAP_CENTER',
    center: Coordinates
}
const mapCenter = (center: Coordinates): ActionMapCenter => ({
    type: 'MAP_CENTER',
    center
})

type ActionMapZoom = {
    type: 'MAP_ZOOM',
    zoom: number
}
const mapZoom = (zoom: number): ActionMapZoom => ({
    type: 'MAP_ZOOM',
    zoom
})

type ActionMapFitBounds = {
    type: 'MAP_FIT_BOUNDS',
    shouldFitBounds: boolean
}
const mapFitBounds = (shouldFitBounds: boolean): ActionMapFitBounds => ({
    type: 'MAP_FIT_BOUNDS',
    shouldFitBounds
})

type ActionMarkersResults = {
    type: 'MARKERS_RESULTS',
    data: StoreLocatorMarkersDTO
}
const actionMarkersResponse = (
    response: StoreLocatorMarkersDTO
): ActionMarkersResults => ({
    type: 'MARKERS_RESULTS',
    data: response
})

type ActionGeocodingError = {
    type: 'GEOCODING_ERROR',
    geocodingError: boolean
}
const geocodingError = (error: boolean): ActionGeocodingError => ({
    type: 'GEOCODING_ERROR',
    geocodingError: error
})

type ActionStoreLocatorError = {
    type: 'STORE_LOCATOR_ERROR',
    error: boolean
}
const actionError = (error: boolean): ActionStoreLocatorError => ({
    type: 'STORE_LOCATOR_ERROR',
    error
})

type ActionProfileResetAddRestaurantSuccess = {
    type: 'PROFILE_RESET_ADD_RESTAURANT_SUCCESS'
}

type ActionClearRestaurantsList = {
    type: 'CLEAR_RESTAURANTS_LIST'
}

const actionClearRestaurantsList = (): ActionClearRestaurantsList => ({
    type: 'CLEAR_RESTAURANTS_LIST'
})

type ActionAlertEnableGeolocation = {
    type: 'SHOW_ALERT_ENABLE_GEOLOCATION',
    isGeolocationEnabled: boolean
}

const showAlertEnableGeolocation = (
    isGeolocationEnabled: boolean
): ActionAlertEnableGeolocation => ({
    type: 'SHOW_ALERT_ENABLE_GEOLOCATION',
    isGeolocationEnabled
})

/* eslint-disable no-use-before-define */
type GetState = () => RootState
type Dispatch = (Action | ThunkAction) => any
type ThunkAction = (Dispatch, GetState) => any
/* eslint-enable no-use-before-define */

const wsMarkers = (dispatch: Dispatch, getState: GetState) => {
    const { selectedServices } = getState().storeLocatorPageState
    let filters = '?'
    for (let filter of selectedServices) {
        filters += `&filters=${filter}`
    }
    return ws
        .get(`${endpoints.storeLocator.markers}${filters}`)
        .then(response => dispatch(actionMarkersResponse(response.data)))
        .catch(error => console.error('Error fetching markers', error))
        .finally(() => dispatch(loading(false)))
}

const wsRestaurant = (
    id: string,
    ecommerceMode: string,
    dispatch: Dispatch,
    fromEcommerce?: boolean
) => {
    const client = fromEcommerce ? getEcommerceAxiosClient(ecommerceMode) : ws
    const endpoint =
        fromEcommerce && ecommerceMode === DELIVERY_IN_APP
            ? endpoints.pages.storeLocator
            : endpoints.pages.restaurant.fetch
    return client
        .get(`${endpoint}/${id}`)
        .then(response => {
            dispatch(selectRestaurant(response.data))
        })
        .catch(error => console.error('Error fetching restaurants', error))
        .finally(() => dispatch(restaurantLoading(false)))
}

let latestWsRestaurantsRequestId: string
const wsRestaurants = async (
    params: SearchParams,
    resultsType: ResultsType,
    dispatch: Dispatch,
    getState: GetState,
    fromDelivery?: boolean,
    fromEcommerce?: boolean
) => {
    const requestId = (Math.random() * 1e9).toString(16)
    latestWsRestaurantsRequestId = requestId
    const isRequestExpired = () => requestId !== latestWsRestaurantsRequestId
    const { selectedServices } = getState().storeLocatorPageState
    const { deliveryFocused } = getState().deliveryPageState
    const { ecommerceMode } = getState().ecommerceOrderState
    const delivery = ecommerceMode === DELIVERY_IN_APP
    const client = fromEcommerce ? getEcommerceAxiosClient(ecommerceMode) : ws
    // FIXME: No platform specific code in shared
    const wsHeaders = client.defaults.headers
    const isWeb = wsHeaders['x-platform'] === 'WEB'
    const {
        address,
        coords,
        radius = 20000,
        limit = 50,
        needsDeliveryStatus
    } = params
    let query = `&lat=${coords.lat}&lng=${coords.lng}&radius=${radius}&limit=${limit}`
    if (fromEcommerce && delivery) {
        query = `${query}&address=${address || ''}`
    }
    let filters = ''
    for (let filter of selectedServices) {
        filters += `&filters=${filter}`
    }
    query = `${query}${filters}`
    if (!address) {
        query = `${query}&isGeolocated=true`
    }
    try {
        const response = await client.get(
            `${endpoints.storeLocator.search}?${query}`
        )
        if (isRequestExpired()) {
            return // if the latest search is not the current one, we can stop here
        }
        let restaurants = response?.data?.results
        if (restaurants && needsDeliveryStatus) {
            restaurants = await wsRestaurantsDeliveryStatus(restaurants)
        }
        if (isRequestExpired()) {
            return // ensure no race condition
        }
        dispatch(
            actionSearchResults(
                address,
                coords,
                resultsType,
                restaurants,
                fromDelivery
            )
        )
        // FIXME: No platform specific code in shared
        if (deliveryFocused && isWeb && window.mmConversionTag) {
            /* Tracking Syzmek : 1346588 = livraison non dispo, 1346589 = livraison dispo */
            const trackingId =
                response.data.results && response.data.results.length > 0
                    ? 1346589
                    : 1346588
            window.mmConversionTag(trackingId, this)
        }
    } catch (error) {
        if (isRequestExpired()) {
            return // this request has been cancelled so we don't want to handle the error
        }
        console.error('Error fetching restaurants', error)
        const { data } = error.response || {}
        if (data) {
            switch (data.message) {
                case errors.INVALID_ADDRESS:
                    throw 'ecommerce.delivery.error.delivery.address'
            }
        }
    } finally {
        if (isRequestExpired()) {
            return //  this request has been cancelled so we don't want to dispatch the loading action
        }
        dispatch(loading(false))
    }
}

const wsRestaurantsDeliveryStatus = async (
    restaurants: Array<OrderRestaurantShortDTO>
) => {
    for (let restaurant of restaurants) {
        const { id } = restaurant
        const response = await deliveryClient.get(
            `${endpoints.pages.storeLocator}/${id}/delivery-status`
        )
        const { deliveryStatus } = response.data
        if (!!deliveryStatus) {
            restaurant = {
                ...restaurant,
                dlvStatus: deliveryStatus
            }
        }
    }
    return restaurants
}

const fetchStoreLocatorPageData = () => {
    return (dispatch: Dispatch) => {
        return ws
            .get(endpoints.pages.storeLocator)
            .then(response => {
                dispatch(pageMetadata(response.data.metaData))
                dispatch(actionStoreLocatorPageDataResponse(response.data))
            })
            .catch(error =>
                console.error('Error fetching store locator page', error)
            )
    }
}

const applyFilters = (services: Array<string>) => {
    return (dispatch: Dispatch, getState: GetState) => {
        dispatch(setSelectedServices(services))
        dispatch(selectRestaurant(null))
        dispatch(loading(true))

        wsMarkers(dispatch, getState).then(() => {
            const {
                geocodedAddress,
                resultsType
            } = getState().storeLocatorPageState
            if (geocodedAddress) {
                wsRestaurants(
                    {
                        coords: geocodedAddress
                    },
                    resultsType || 'search',
                    dispatch,
                    getState
                )
            }
        })
    }
}

const selectMarker = (restaurantId: string, fromEcommerce?: boolean) => {
    return (dispatch: Dispatch, getState: GetState) => {
        const {
            activeRestaurant,
            restaurants
        } = getState().storeLocatorPageState
        const { ecommerceMode } = getState().ecommerceOrderState

        if (activeRestaurant && activeRestaurant.id === restaurantId) {
            return
        }

        dispatch(actionSelectRestaurant(null))

        if (restaurants) {
            const restaurant = restaurants.find(
                (r: RestaurantShortDTO) => r && r.id === restaurantId
            )
            if (restaurant) {
                dispatch(selectRestaurant(restaurant))
                return
            }
        }
        dispatch(restaurantLoading(true))
        wsRestaurant(restaurantId, ecommerceMode, dispatch, fromEcommerce)
    }
}

const selectRestaurant = (restaurant: ?RestaurantShortDTO) => {
    return (dispatch: Dispatch, getState: GetState) => {
        if (restaurant) {
            const { activeRestaurant } = getState().storeLocatorPageState
            if (activeRestaurant && activeRestaurant.id === restaurant.id) {
                dispatch(actionSelectRestaurant(null))
                return
            }
            const { lat, lng } = restaurant
            dispatch(mapCenter({ lat, lng }))
            dispatch(mapZoom(16))
            dispatch(visitRestaurant(restaurant))
            dispatch(actionSelectRestaurant(restaurant))
        }
    }
}

const visitRestaurant = (restaurant: RestaurantShortDTO) => {
    return (dispatch: Dispatch, getState: GetState) => {
        let { visitedRestaurants } = getState().storeLocatorPageState
        visitedRestaurants = visitedRestaurants || []
        const max = 3
        if (
            visitedRestaurants.some(
                r => r && restaurant && r.id === restaurant.id
            )
        ) {
            return
        }
        visitedRestaurants.unshift(restaurant)
        visitedRestaurants = visitedRestaurants.slice(0, max)

        dispatch(setVisitedRestaurants(visitedRestaurants))
    }
}

const search = (
    params: SearchParams,
    type: ResultsType,
    fromDelivery?: boolean,
    fromEcommerce?: boolean
) => {
    return (dispatch: Dispatch, getState: GetState) => {
        if (params.filters && params.filters.length > 0) {
            dispatch(setSelectedServices(params.filters))
        }
        dispatch(loading(true))
        dispatch(selectRestaurant(null))

        if (params.filters) {
            wsMarkers(dispatch, getState)
        }

        return wsRestaurants(
            params,
            type,
            dispatch,
            getState,
            fromDelivery,
            fromEcommerce
        ).catch((error: string) => {
            throw error
        })
    }
}

const searchAll = () => {
    return (dispatch: Dispatch) => {
        return ws
            .get(endpoints.storeLocator.all)
            .then(response => {
                dispatch(actionSearchAllResults(response.data))
            })
            .catch(error => {
                console.error('Error fetching all restaurants :', error)
                if (error === errors.NETWORK)
                    dispatch(actionNetworkError({ type: errors.NETWORK }))
            })
            .finally(() => dispatch(loading(false)))
    }
}

export {
    actionClearRestaurantsList,
    actionSelectRestaurant,
    actionMarkersResponse,
    fetchStoreLocatorPageData,
    actionError,
    search,
    searchAll,
    clearSearch,
    selectRestaurant,
    toggleRestaurant,
    selectMarker,
    setUserPosition,
    geocodingError,
    centerOnUserPosition,
    mapCenter,
    mapZoom,
    mapFitBounds,
    applyFilters,
    resetServices,
    loading,
    restaurantLoading,
    showAlertEnableGeolocation,
    reset
}

export type Action =
    | ActionStoreLocatorPageDataResponse
    | ActionSelectedServices
    | ActionResetServices
    | ActionSearchResults
    | ActionSearchAllResults
    | ActionClearSearch
    | ActionMarkersResults
    | ActionSelectRestaurant
    | ActionToggleRestaurant
    | ActionSetUserPosition
    | ActionCenterOnUserPosition
    | ActionSetVisitedRestaurants
    | ActionLoading
    | ActionRestaurantLoading
    | ActionMapCenter
    | ActionMapZoom
    | ActionMapFitBounds
    | ActionGeocodingError
    | ActionMetadata
    | ActionAPIError
    | ActionNetworkError
    | ActionStoreLocatorError
    | ActionProfileResetAddRestaurantSuccess
    | ActionAlertEnableGeolocation
    | ActionReset
    | ActionClearRestaurantsList
